import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useHighestRole } from '../../auth/useAuth';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import { EmptyState } from '../../design-system/components/EmptyState';
import { DataTable, type DataTableColumn } from '../../design-system/components/DataTable';
import { DateField } from '../../design-system/components/DateField';
import { getRuntimeConfig } from '../../config/runtime-config';
import { useActiveSite } from '../../state/useActiveSite';
import { fetchAudit } from '../../api/v1-client';
import type { AuditEntry, SiteId } from '../../api/v1';
import { formatDateTime } from '../../utils/date-format';
import './AuditScreen.css';
import { NoSiteNotice } from '../../shell/NoSiteNotice';
import { HelpScreenNote } from '../../help';
import { formatActor, actorTitle, actorSearchText } from './format-actor';
import { auditActionFilterLabel, auditActionLabel } from './audit-action-label';
import { AUDIT_OUTCOME_LABEL, auditTargetText } from './audit-target';

const PAGE_SIZE = 50;
// Safety cap on how many pages the "quick period" auto-loader will
// pull to try to cover the selected range — bounds worst case to
// ~1000 entries instead of paging back through a site's entire
// history if the operator picks "Last 7 days" on a very busy site.
const MAX_AUTO_LOAD_PAGES = 20;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** `YYYY-MM-DD` for a local calendar date `daysAgo` days before today. */
function localIsoDate(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Start-of-day epoch ms for a `YYYY-MM-DD` value, in local time
 *  (built from date components directly rather than `new Date(str)`,
 *  which would parse as UTC and shift the boundary by a timezone
 *  offset). */
function localDateStartMs(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0).getTime();
}

/** End-of-day (23:59:59.999) epoch ms for a `YYYY-MM-DD` value, local time. */
function localDateEndMs(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999).getTime();
}

type Preset = 'today' | '3d' | '7d' | null;

export function AuditScreen() {
  const role = useHighestRole();
  const flags = getRuntimeConfig().featureFlags;
  const readOnly = role === 'nexsoss:site_supervisor';
  const { site } = useActiveSite();

  const [preset, setPreset] = useState<Preset>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<'all' | AuditEntry['outcome']>('all');
  const [actionFilter, setActionFilter] = useState<'all' | string>('all');
  const [searchText, setSearchText] = useState('');

  const query = useInfiniteQuery({
    queryKey: ['v1', site?.id ?? '__none__', 'audit', 'current', '1.0'] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchAudit(site?.id as SiteId, pageParam, PAGE_SIZE),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: site !== null,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const pages = query.data?.pages ?? [];
  const entries: AuditEntry[] = pages.flatMap((p) => p.entries);
  const oldestLoadedMs = entries.length > 0 ? entries[entries.length - 1]!.at_ms : null;
  const fromBoundMs = fromDate ? localDateStartMs(fromDate) : null;
  const toBoundMs = toDate ? localDateEndMs(toDate) : null;

  // Entries load newest-first, 50 at a time. A "Last 7 days" filter
  // only reflects reality once enough history is actually loaded, so
  // keep pulling pages while there's a `from` bound, more pages
  // exist, and the oldest entry loaded so far is still newer than
  // that bound — capped so a busy site's full history is never
  // paged through by accident.
  useEffect(() => {
    if (fromBoundMs === null) return;
    if (!hasNextPage || isFetchingNextPage) return;
    if (oldestLoadedMs !== null && oldestLoadedMs <= fromBoundMs) return;
    if (pages.length >= MAX_AUTO_LOAD_PAGES) return;
    void fetchNextPage();
  }, [fromBoundMs, oldestLoadedMs, pages.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  function applyPreset(p: 'today' | '3d' | '7d') {
    setPreset(p);
    const daysBack = p === 'today' ? 0 : p === '3d' ? 2 : 6;
    setFromDate(localIsoDate(daysBack));
    setToDate(localIsoDate(0));
  }

  const availableActions = useMemo(
    () => Array.from(new Set(entries.map((e) => e.action))).sort(),
    [entries],
  );

  const filtered = useMemo(
    () =>
      entries.filter((e) => {
        if (fromBoundMs !== null && e.at_ms < fromBoundMs) return false;
        if (toBoundMs !== null && e.at_ms > toBoundMs) return false;
        if (outcomeFilter !== 'all' && e.outcome !== outcomeFilter) return false;
        if (actionFilter !== 'all' && e.action !== actionFilter) return false;
        if (searchText.trim()) {
          const q = searchText.trim().toLowerCase();
          const haystack = `${actorSearchText(e)} ${e.action} ${auditActionLabel(e) ?? ''} ${auditTargetText(e)}`.toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      }),
    [entries, fromBoundMs, toBoundMs, outcomeFilter, actionFilter, searchText],
  );

  const hasActiveFilters =
    fromDate !== '' || toDate !== '' || outcomeFilter !== 'all' || actionFilter !== 'all' || searchText.trim() !== '';

  function clearFilters() {
    setPreset(null);
    setFromDate('');
    setToDate('');
    setOutcomeFilter('all');
    setActionFilter('all');
    setSearchText('');
  }

  if (!site) {
    return (
      <div className="nx-audit">
        <h1 className="nx-section-title">Audit</h1>
        <NoSiteNotice />
      </div>
    );
  }

  function exportCsv() {
    const header = 'timestamp,actor,actor_sub,action,target,outcome';
    const rows = filtered.map((e) =>
      [new Date(e.at_ms).toISOString(), formatActor(e), e.actor_sub, e.action, JSON.stringify(e.target), e.outcome].join(','),
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const columns: DataTableColumn<AuditEntry>[] = [
    { id: 'at', header: 'Timestamp', cell: (e) => formatDateTime(e.at_ms), sortable: true, sortValue: (e) => e.at_ms },
    {
      id: 'actor',
      header: 'Actor',
      cell: (e) => <span title={actorTitle(e)}>{formatActor(e)}</span>,
      sortable: true,
      // Sorted by what is on screen, not by the hidden subject: a
      // column that sorts into an order the reader cannot see is worse
      // than one that does not sort.
      sortValue: (e) => formatActor(e),
    },
    {
      id: 'action',
      header: 'Action',
      // A sentence where the action has one ("Viewed console as FOH
      // operator"), the code otherwise; the code stays in the tooltip
      // and the CSV export.
      cell: (e) => {
        const label = auditActionLabel(e);
        return label ? (
          <span title={e.action} data-testid="audit-action-label">
            {label}
          </span>
        ) : (
          <code>{e.action}</code>
        );
      },
      sortable: true,
      sortValue: (e) => auditActionLabel(e) ?? e.action,
    },
    // A schedule or a blocked time by its name; the Action sentence
    // says what changed (`auditTargetText`).
    { id: 'target', header: 'Target', cell: (e) => auditTargetText(e), sortable: true, sortValue: (e) => auditTargetText(e) },
    // In the outcome filter's words, as My activity shows the same rows
    // (final gate, 2026-10-31): the cell read "timed_out" under a filter
    // offering "Timed out". The code stays in the CSV export.
    {
      id: 'outcome',
      header: 'Outcome',
      cell: (e) => AUDIT_OUTCOME_LABEL[e.outcome] ?? e.outcome,
      sortable: true,
      sortValue: (e) => e.outcome,
    },
  ];

  return (
    <div className="nx-audit nx-page-fill">
      <h1 className="nx-section-title">Audit</h1>
      <HelpScreenNote screen="audit" />
      {query.isError && (
        <AlertBanner variant="error" title="Failed to load audit log">
          {query.error instanceof Error ? query.error.message : 'Unknown error'}
        </AlertBanner>
      )}

      <div className="nx-audit__filters">
        <div className="nx-audit__period-group" role="tablist" aria-label="Quick period">
          {(['today', '3d', '7d'] as const).map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={preset === p}
              className={`nx-audit__period-btn${preset === p ? ' nx-audit__period-btn--active' : ''}`}
              onClick={() => applyPreset(p)}
              data-testid={`audit-period-${p}`}
            >
              {p === 'today' ? 'Today' : p === '3d' ? 'Last 3 days' : 'Last 7 days'}
            </button>
          ))}
        </div>

        {/* The shared design-system field: this screen's inline copy of
            the masked-overlay pattern was promoted so the dispatch form
            and the calendar could use the same one. */}
        <DateField
          className="nx-audit__date-filter"
          label="From"
          value={fromDate}
          onChange={(v) => {
            setPreset(null);
            setFromDate(v);
          }}
          ariaLabel="Filter audit log from date"
          data-testid="audit-from-date"
        />
        <DateField
          className="nx-audit__date-filter"
          label="To"
          value={toDate}
          onChange={(v) => {
            setPreset(null);
            setToDate(v);
          }}
          ariaLabel="Filter audit log to date"
          data-testid="audit-to-date"
        />

        <input
          type="text"
          className="nx-audit__search"
          placeholder="Search actor, action, target…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          data-testid="audit-search"
        />
        <select
          value={outcomeFilter}
          onChange={(e) => setOutcomeFilter(e.target.value as 'all' | AuditEntry['outcome'])}
          data-testid="audit-filter-outcome"
        >
          <option value="all">All outcomes</option>
          {(Object.keys(AUDIT_OUTCOME_LABEL) as AuditEntry['outcome'][]).map((o) => (
            <option key={o} value={o}>
              {AUDIT_OUTCOME_LABEL[o]}
            </option>
          ))}
        </select>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          data-testid="audit-filter-action"
        >
          <option value="all">All actions</option>
          {availableActions.map((a) => (
            <option key={a} value={a}>
              {auditActionFilterLabel(a)}
            </option>
          ))}
        </select>

        {hasActiveFilters && (
          <button type="button" className="nx-audit__filters-clear" onClick={clearFilters}>
            Clear filters
          </button>
        )}

        {flags.auditExportEnabled && !readOnly && (
          <button
            type="button"
            className="nx-audit__export"
            onClick={exportCsv}
            disabled={filtered.length === 0}
          >
            Export CSV
          </button>
        )}
      </div>

      {fromBoundMs !== null && query.isFetchingNextPage && (
        <p className="nx-audit__loading-hint">Loading older entries to cover the selected range…</p>
      )}

      {query.isLoading ? (
        <p>Loading…</p>
      ) : entries.length === 0 ? (
        <EmptyState title="No audit events" />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No audit events match the current filters"
          description="Try a different period, or clear the search and filter selections above."
        />
      ) : (
        // Only the rows scroll; the filters above and "Load more"
        // below stay put. Previously "Load more" walked further down
        // the page with every page fetched, so the control you needed
        // most was the one that kept running away.
        <div className="nx-page-fill__body">
          <DataTable columns={columns} rows={filtered} getRowId={(e) => e.audit_id} ariaLabel="Audit events" />
          {query.hasNextPage && (
            <button
              type="button"
              className="nx-audit__load-more"
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
